import { useMemo, useCallback, useRef } from 'react';
import { useTerminalsContext } from 'contexts/TerminalsProvider';
import { FieldDependencies, FieldSynonyms } from '../entities/HideableOrderField';

// Local cache for computed visibility data
const visibilityCache = new Map();

const addSynonyms = (fieldsSet) => {
	const result = new Set(fieldsSet);

	fieldsSet.forEach((fieldName) => {
		const synonyms = FieldSynonyms[fieldName];
		if (synonyms) {
			synonyms.forEach((synonym) => result.add(synonym));
		}
	});

	return result;
};

const computeHiddenFields = (terminal) => {
	const baseHiddenFields = terminal?.hidden_order_fields || [];
	let allHidden = new Set(baseHiddenFields);

	// Add dependent fields
	baseHiddenFields.forEach((fieldName) => {
		const dependentFields = FieldDependencies[fieldName];
		if (dependentFields) {
			dependentFields.forEach((depField) => allHidden.add(depField));
		}
	});

	// Add synonyms/aliases for any hidden field
	allHidden = addSynonyms(allHidden);

	return Array.from(allHidden);
};

const getCacheKey = (terminalId, hiddenFields) => {
	return `${terminalId}:${hiddenFields?.join(',') || ''}`;
};

export const useTerminalFieldVisibility = (terminalId) => {
	const { data: terminals } = useTerminalsContext();
	const cacheKeyRef = useRef(null);

	const currentTerminal = useMemo(() => {
		return terminals?.[terminalId];
	}, [terminals, terminalId]);

	// Get base hidden fields from terminal config
	const baseHiddenFields = useMemo(() => {
		return currentTerminal?.hidden_order_fields || [];
	}, [currentTerminal]);

	// Calculate all hidden fields including dependencies with caching
	const hiddenFields = useMemo(() => {
		const newCacheKey = getCacheKey(terminalId, baseHiddenFields);

		// Return cached result if available and cache key hasn't changed
		if (cacheKeyRef.current === newCacheKey && visibilityCache.has(newCacheKey)) {
			return visibilityCache.get(newCacheKey);
		}

		// Compute new result
		const computed = computeHiddenFields(currentTerminal);

		// Update cache
		cacheKeyRef.current = newCacheKey;
		visibilityCache.set(newCacheKey, computed);

		// Clean old cache entries if cache grows too large
		if (visibilityCache.size > 50) {
			const firstKey = visibilityCache.keys().next().value;
			visibilityCache.delete(firstKey);
		}

		return computed;
	}, [terminalId, baseHiddenFields, currentTerminal]);

	const isFieldHidden = useCallback(
		(fieldName) => {
			if (!fieldName) return false;
			return hiddenFields.includes(fieldName);
		},
		[hiddenFields]
	);

	const isAnyFieldHidden = useCallback(
		(...fieldNames) => {
			return fieldNames.some((fieldName) => isFieldHidden(fieldName));
		},
		[isFieldHidden]
	);

	const isAllFieldsHidden = useCallback(
		(...fieldNames) => {
			return fieldNames.every((fieldName) => isFieldHidden(fieldName));
		},
		[isFieldHidden]
	);

	const getVisibilityProps = useCallback(
		(fieldName) => {
			return {
				hidden: isFieldHidden(fieldName),
			};
		},
		[isFieldHidden]
	);

	const getGroupVisibilityProps = useCallback(
		(...fieldNames) => {
			return {
				hidden: isAnyFieldHidden(...fieldNames),
			};
		},
		[isAnyFieldHidden]
	);

	return {
		currentTerminal,
		hiddenFields,
		isFieldHidden,
		isAnyFieldHidden,
		isAllFieldsHidden,
		getVisibilityProps,
		getGroupVisibilityProps,
	};
};
